import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.scss';
import Content from './Content';
import axios from './axios-instance';
import { withRouter } from 'react-router';
import Wallet from './components/Wallet/Wallet';
import Error from './components/UI/Error/Error';
import ReactGA from 'react-ga4';
import { withCookies, useCookies } from 'react-cookie';
import { useSelector, useDispatch } from 'react-redux';
import { updateDeviceDetails } from './store/deviceSlice';
import { mobileCheck } from './utils/mobileCheck';
import { DEV_MODE } from './dev/devMode';

// Device-id generator. crypto.randomUUID() needs a secure context (https/localhost)
// and a modern engine; fall back to a v4 string elsewhere so first-visit device
// registration never throws and hangs the loader on http/legacy browsers.
const safeUuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        try { return crypto.randomUUID(); } catch (e) { /* secure-context-gated; fall through */ }
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

const App = (props) => {
    const { location, ua } = props;
    const [
        cookies,
        setCookie,
        // removeCookie
    ] = useCookies(['A8FPVID', 'A8DUUID', 'A8CFVLH']); 

    const deviceSelector = useSelector((state) => state.device);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [error, setShowError] = useState({
        showError: false,
        errorCode: null,
        title : null,
        description : null
    });
    const [hasSlug, setHasSlug] = useState(null);
    const [catchUrlSearch, setCatchUrlSearch] = useState(null);
    const [gdpr, setGdpr] = useState(null);
    const [gdprConsent, setGdprConsent] = useState(null);
    const [forwardParams, setForwardParams] = useState(null);
    const [preciseLocation, setPreciseLocation] = useState(null)
    const [showWallet, setShowWallet] = useState(false);

    const [showActv8, setShowActv8] = useState(false);
    const [timestamp, setTimestamp] = useState(null);

    const deviceDetails = useMemo(() => {
        const deviceToUpdate = {};

        deviceToUpdate.uuid = null;
        deviceToUpdate.a8GeoIp = cookies.A8CFVLH;
        deviceToUpdate.os_name = ua?.os?.name;
        deviceToUpdate.os_version = ua?.os?.version;
        deviceToUpdate.manufacturer = ua?.device?.vendor;
        deviceToUpdate.model = ua?.device?.model;
        deviceToUpdate.locale = navigator?.language ?? 'en-us';
        deviceToUpdate.isMobile = mobileCheck();
        deviceToUpdate.isIphone =
            navigator?.userAgent?.toLowerCase()?.indexOf('iphone') > -1;

        return deviceToUpdate;
    }, [cookies.A8FPVID, cookies.A8CFVLH, ua]);
    
    const params = new URLSearchParams(location.search);

    const setGoogleAnalytics = () => {
        // Dev mode: never touch real analytics.
        if (DEV_MODE) return;
        if (
            process.env.REACT_APP_GOOGLE_ANALYTICS_PROPERTY_ID !== undefined &&
            process.env.REACT_APP_GOOGLE_ANALYTICS_PROPERTY_ID !== null
        ) {
            ReactGA.initialize(
                process.env.REACT_APP_GOOGLE_ANALYTICS_PROPERTY_ID
            );
            ReactGA.send('pageview');
        }
    }

    // Loading watchdog. The old page-load observer captured stale state in its
    // closure so its timeout could never fire — users on a hung request just
    // watched the loader dots forever. Now: while `loading` is true, arm a
    // timer; if it fires, swap the loader for the Error screen (which has a
    // Try Again reload button).
    useEffect(() => {
        if (!loading) return undefined;
        const timeOutValue = Number(process.env.REACT_APP_TIMEOUT_VALUE) || 15000;
        const timer = setTimeout(() => {
            setShowError({
                showError: true,
                errorCode: 'timeout',
                title: 'This is taking longer than expected',
                description:
                    'We couldn’t load your content in time. Check your connection and try again.',
            });
            setLoading(false);
        }, timeOutValue);
        return () => clearTimeout(timer);
    }, [loading]);

    // const getUuid = () => {
    //     console.log('Getting UUID...');   

    //     const uuid = btoa(uuidv4());
        
    //     const cookieMaxAge = process.env
    //         .REACT_APP_COOKIE_A8FPVID_EXPIRATION
    //         ? process.env.REACT_APP_COOKIE_A8FPVID_EXPIRATION *
    //             (60 * 60 * 24)
    //         : (60 * 60 * 24); // (60 seconds * 60 minutes * 24 hours)

    //     setCookie('A8FPVID', uuid, {
    //         maxAge: cookieMaxAge,
    //         path: '/',
    //         sameSite: true,
    //     });

    //     return uuid;

    // }

    // TEST Jenkins 
    
    const getSl = () => {


        axios.get(
            `/shortlink?slug=${params.get('sl')}`
        ).then((res)=>{
            if (res && res.data) {
                console.log('Long url is' + res.data.url);
                let search = new URL(res.data.url).search;
                let fullUrlParams = new URLSearchParams(search);

                let fp_enabled = fullUrlParams.has('fp_enabled') && fullUrlParams.get('fp_enabled') === 'true'
                let geolocation = fullUrlParams.has('geolocation') && fullUrlParams.get('geolocation') === 'true'
                
                setCatchUrlSearch(search);
                getUuid(deviceDetails, geolocation, fp_enabled);

                // if(fullUrlParams.has('fp_enabled') && fullUrlParams.get('fp_enabled') === 'true') {
                //     getUuid(deviceDetails, false, true);
                // }

                // if(fullUrlParams.has('geolocation') && fullUrlParams.get('geolocation') === 'true') {
                //     console.log('Request precise location: ' + fullUrlParams.get('geolocation'))
                //     getLocation(search, deviceDetails)
                // } else {
                //     console.log('Proceed with approx location')
                //     setCatchUrlSearch(search);
                //     // Register device
                //     console.log(deviceDetails)
                //     registerDevice(deviceDetails);
                // }                

                
            } else {
                setShowError({
                    showError: true,
                    errorCode: 404,
                });
                setLoading(false);
            }
        }).catch((err)=>{
            setShowError({
                showError: true,
                errorCode: 404,
            });
            setLoading(false);
        });
    };

    const extractForwardParams = () => {
        params.delete('sl');
        params.delete('geolocation');
        // Set forward params with non-actv8 params
        setForwardParams(params.toString());
    };

    const checkSlug = () => {
        console.log('Checking slug...');
        if (params.has('gdpr') && params.has('gdpr_consent')) {
            setGdpr(params.get('gdpr'));
            setGdprConsent(params.get('gdpr_consent'));
        }
        if (params.has('ts')) {
            setTimestamp(parseInt(params.get('ts')));
        }
        if (params.has('sl')) {
            setHasSlug(true);
            getSl(params);
        } else {
            setHasSlug(false);
        }
        extractForwardParams();
    };

    const checkParams = () => {
        console.log('checking  params...');
        const paramsString = window.location.search;
        const params = new URLSearchParams(paramsString);
        if (
            !params.has('sl') &&
            (!params.has('triggerable_id') || !params.has('client_id'))
        ) {
            setShowWallet(true);
        } else {
            setShowWallet(false);
        }
    };

    const registerDevice = useCallback(
        (device) => {
            !loading && setLoading(true);
            if (!deviceSelector?.deviceDetails?.id) {
                let endpoint = `device/`;
                if (gdpr) {
                    endpoint += `?gdpr=${gdpr}`;
                    endpoint += `&gdpr_consent=${gdprConsent}`;
                }

                axios.post(endpoint, device, {
                    headers: {
                        'x-device-uuid': `${device.uuid}`,
                    },
                }).then((res)=>{
                    if (res && res.data) {
                        const deviceToUpdate = {
                            ...res?.data,
                            isMobile: deviceDetails?.isMobile,
                            isIphone: deviceDetails?.isIphone,
                        };

                        dispatch(updateDeviceDetails(deviceToUpdate));
                        checkParams();
                    }
                }).catch((err)=>{
                    if (err.message === 'Request failed with status code 403') {
                        setShowError({
                            showError: true,
                            errorCode: 403,
                        });
                        setLoading(false);
                    } else {
                        setShowError({
                            showError: true,
                            errorCode: err.status,
                        });
                        setLoading(false);
                    }
                    console.error('ERROR: ', err.message);
                });

            }
        },
        [
            loading,
            deviceSelector?.deviceDetails?.id,
            gdpr,
            gdprConsent,
            deviceDetails?.isMobile,
            deviceDetails?.isIphone,
            dispatch,
        ]
    );

    const getLocation = (device) =>{
            if (!('geolocation' in navigator)) {
                console.log('geolocation not available');
                registerDevice(deviceDetails);
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    let deviceWithLocation = {...device}
                    deviceWithLocation.latitude = position.coords.latitude;
                    deviceWithLocation.longitude = position.coords.longitude;
                    setPreciseLocation(position)
                    registerDevice(deviceWithLocation);
                },
                (err) => {
                    // Denied / unavailable / timed out — register anyway with
                    // approximate (IP-based) location.
                    console.log(err);
                    registerDevice(device);
                }
            );
     }

    const getUuid = useCallback(
        async (deviceToUpdate, geolocationEnabled = false, fpEnabled = false) => {
            console.log('Getting uuid...');

            const cookieMaxAge = process.env.REACT_APP_FPJS_COOKIE_MAX_AGE_DAYS
                ? process.env.REACT_APP_FPJS_COOKIE_MAX_AGE_DAYS * (60 * 60 * 24)
                : (60 * 60 * 24); // (60 seconds * 60 minutes * 24 hours)

            // The trigger's Full URL decides both flags up front (fp_enabled,
            // geolocation) — branch the whole boot experience off them here.
            // When precise location isn't requested, we register immediately
            // with approximate (IP-based) location.
            const proceed = (device) => {
                if (geolocationEnabled) {
                    getLocation(device);
                } else {
                    registerDevice(device);
                }
            };

            // Local (non-fingerprint) device id — the normal path when
            // fp_enabled=false, and the FALLBACK whenever fingerprinting can't
            // deliver a visitorId. Registration must never strand the loader.
            const registerWithLocalUuid = () => {
                deviceToUpdate.uuid = cookies.A8DUUID ?? btoa(safeUuid());
                setCookie('A8DUUID', deviceToUpdate.uuid, {
                    maxAge: cookieMaxAge,
                    path: '/',
                    sameSite: true,
                });
                proceed(deviceToUpdate);
            };

            if (!fpEnabled || !process.env.REACT_APP_FPJS_ENABLED || process.env.REACT_APP_FPJS_ENABLED === 'FALSE') {
                console.log('Generating uuid');
                registerWithLocalUuid();
            } else if((process.env.REACT_APP_FPJS_ENABLED && process.env.REACT_APP_FPJS_ENABLED === 'TRUE')){
                if(cookies.A8FPVID) {
                    console.log('FP found in cookie.');
                    deviceToUpdate.uuid = cookies.A8FPVID;
                    setCookie('A8FPVID', deviceToUpdate.uuid, {
                        maxAge: cookieMaxAge,
                        path: '/',
                        sameSite: true,
                    });
                    proceed(deviceToUpdate);
                } else {
                    console.log('fingerprinting...');

                    let fpPromiseParams = {
                        token: process.env.REACT_APP_FPJS_TOKEN,
                    };

                    if (process.env.REACT_APP_FPJS_ENDPOINT !== null) {
                        fpPromiseParams.endpoint = process.env.REACT_APP_FPJS_ENDPOINT;
                        fpPromiseParams.scriptUrlPattern = process.env.REACT_APP_FPJS_SCRIPT_URL_PATTERN
                    } else {
                        // Misconfiguration must degrade, not strand: register
                        // with a local uuid instead of leaving the loader spinning.
                        console.log('Missing FPJS endpoint — falling back to local uuid');
                        registerWithLocalUuid();
                        return;
                    }

                    // Lazy-load the FingerprintJS Pro SDK only when fingerprinting
                    // is actually invoked (fp_enabled param + env on + no cached
                    // visitor cookie). Keeps the ~heavy SDK out of the main bundle
                    // for the common no-FP path. ponytail: dynamic import, code-split.
                    import('@fingerprintjs/fingerprintjs-pro')
                      .then(({ default: FingerprintJS }) => FingerprintJS.load(fpPromiseParams))
                      .then(fp => fp.get()).then(result => {
                        if (result.visitorId) {
                            deviceToUpdate.uuid = result.visitorId;
                            console.log(deviceToUpdate.uuid)
                            setCookie('A8FPVID', deviceToUpdate.uuid, {
                                maxAge: cookieMaxAge,
                                path: '/',
                                sameSite: true,
                            });
                            proceed(deviceToUpdate);
                        } else {
                            console.log('Device uuid could not be calculated — falling back to local uuid');
                            registerWithLocalUuid();
                        }
                      })
                      .catch(fpError => {
                        // SDK/network failure (offline, CORS, blocked script):
                        // degrade to a local uuid so the content still loads.
                        console.error(fpError);
                        registerWithLocalUuid();
                      })

                      return;
                }

            } else {
                setShowError({
                    showError: true,
                    errorCode: 'Device identification failed',
                    title: null,
                    description: 'There was a problem identifying your device.',
                });
            }
            console.log('completed fetching...');
            console.log(deviceToUpdate);
        },
        [dispatch, getLocation, setCookie, registerDevice]
    );


    useEffect(() => {
        const { cookieEnabled } = navigator;
        console.log('Cookie enabled : ' + cookieEnabled);

        if(!cookieEnabled) {
            setShowError({
                showError: true,
                errorCode: 'Cookies disabled',
                title: null,
                description: 'This site requires cookies to be enabled. Please enable them and try again.',
            });
            setLoading(false);
            return;
        }

        // Sets google analytics
        setGoogleAnalytics();

        // VCheck slug
        checkSlug();
        
        // geolocationEnabled ? getLocation(deviceDetails) : registerDevice(deviceDetails);
        // eslint-disable-next-line react-hooks/exhaustive-deps

    }, []);


    return (
        <div className='App'>
            {showWallet ? (
                <Wallet device={deviceSelector} />
            ) : error.showError ? (
                <Error status={error.errorCode} title={error.title} description={error.description}/>
            ) : (
                <Content
                    hasSlug={hasSlug}
                    onNoThanks={() => setShowActv8(false)}
                    device={deviceSelector}
                    forwardParams={forwardParams}
                    showActv8={showActv8}
                    location={location}
                    preciseLocation={preciseLocation}
                    catchUrlSearch={catchUrlSearch}
                    setShowError={setShowError}
                    setLoading={setLoading}
                    timestamp={timestamp}
                />
            )}
        </div>
    );
};
export default withCookies(withRouter(App));
